import DrawInteraction from '@planet/maps/interaction/Draw';

import VectorLayer from '@planet/maps/layer/Vector';
import VectorSource from '@planet/maps/source/Vector';

import isEqual from 'lodash/isEqual';
import {Collection} from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import {Style} from 'ol/style';
import {array, bool, func, object, string} from 'prop-types';
import React from 'react';
import ExplorerModifyInteraction from './ModifyInteraction.jsx';
import {DRAW_INTERACTIONS} from './interactions.js';
import {MAP_TOOLS} from '@app/constants';
import {geometryStyle, getVertexStyle} from '@app/util/map-edit-styles';

const getMapDivFromEvent = evt => evt.target.map_.getTarget();

class EditableVectorLayer extends React.Component {
  constructor(props) {
    super(props);
    this.sourceRef = React.createRef();
  }

  shouldComponentUpdate(nextProps) {
    const {setTempGeometry, drawTool, features, onAddFeature, visible} =
      this.props;
    const shouldUpdate =
      visible !== nextProps.visible ||
      !isEqual(nextProps.drawTool, drawTool) ||
      nextProps.features !== features ||
      !isEqual(nextProps.onAddFeature, onAddFeature) ||
      !isEqual(nextProps.setTempGeometry, setTempGeometry);

    return shouldUpdate;
  }

  render() {
    const {
      drawTool = {},
      features = [],
      onAddFeature,
      setTempGeometry,
    } = this.props;
    const active = drawTool.layer === this.props.layerName;
    const isEditing = active && drawTool.name === MAP_TOOLS.EDIT;

    const format = new GeoJSON();
    const olFeatures = format.readFeatures({
      type: 'FeatureCollection',
      features,
    });

    const updateDrawFeature = feature => {
      setTempGeometry(format.writeGeometryObject(feature.getGeometry()));
    };

    return (
      <React.Fragment>
        {active && !!DRAW_INTERACTIONS[drawTool.name] && (
          <DrawInteraction
            key={drawTool.name}
            options={DRAW_INTERACTIONS[drawTool.name]}
            onDrawStart={evt => {
              if (evt.feature) {
                evt.feature.on('change', () => updateDrawFeature(evt.feature));
                updateDrawFeature(evt.feature);
              }
            }}
            onDrawEnd={evt => {
              // clear out the temp geometry
              setTempGeometry(null);

              const newFeature = format.writeFeatureObject(evt.feature);
              newFeature.id = new Date().getTime();
              onAddFeature(newFeature);
            }}
          />
        )}
        {isEditing && (
          <ExplorerModifyInteraction
            key="modify"
            options={{
              features: new Collection(
                olFeatures.filter(f => f.getId() === drawTool.target)
              ),
              style: new Style(getVertexStyle(4)),
            }}
            onModifyStart={evt => {
              getMapDivFromEvent(evt).style.cursor = 'grabbing';
            }}
            onModifyEnd={evt => {
              getMapDivFromEvent(evt).style.cursor = 'grab';
              evt.features.forEach(feature => updateDrawFeature(feature));
            }}
            onMouseEnter={evt => {
              getMapDivFromEvent(evt).style.cursor = 'grab';
            }}
            onMouseLeave={evt => {
              getMapDivFromEvent(evt).style.cursor = 'default';
            }}
          />
        )}

        <VectorLayer
          style={geometryStyle(drawTool.target || '')}
          key={this.props.layerName}
          visible={this.props.visible ?? true}
        >
          <VectorSource ref={this.sourceRef} features={olFeatures} />
        </VectorLayer>
      </React.Fragment>
    );
  }
}

EditableVectorLayer.propTypes = {
  onAddFeature: func,
  setTempGeometry: func,
  drawTool: object,
  layerName: string,
  features: array,
  visible: bool,
};

export default EditableVectorLayer;
